// @oasis-permission-inherits: /projects (projects:view)
import { skipToken, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ChevronDown,
  ChevronLeft,
  CheckCircle2,
  Clock,
  Download,
  FileJson,
  Layers,
  MoreHorizontal,
  Smartphone,
  User,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import { PermissionGuard } from "~/components/permission-guard";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { Skeleton } from "~/components/ui/skeleton";
import { copyToClipboard } from "~/lib/clipboard";
import { optionalTrueSearchParamSchema } from "~/modules/long-to-short/search-params";
import { CloneProjectToUserButton } from "~/modules/projects/components/clone-project-to-user-button";
import { CopyableId } from "~/modules/projects/components/copyable-id";
import {
  JerboaProjectRendererPreview,
  type JerboaProjectRendererPreviewHandle,
} from "~/modules/projects/components/jerboa-project-renderer-preview";
import { preloadProjectPlaybackProto } from "~/modules/projects/components/jerboa-project-web-player-preview";
import { ProjectAssetStore } from "~/modules/projects/components/project-asset-store";
import { ProjectChatThreadsPanel } from "~/modules/projects/components/project-chat-threads-panel";
import { ProjectClientExportButton } from "~/modules/projects/components/project-client-export-button";
import { ProjectOverviewSection } from "~/modules/projects/components/project-overview-section";
import { ProjectPlaybackTimeline } from "~/modules/projects/components/project-playback-timeline";
import { editorProjectDetailsToPlayback } from "~/modules/projects/lib/editor-project-details-to-playback";
import {
  collectCommittedSyncTokens,
  findProjectChatActionsForSyncToken,
} from "~/modules/projects/lib/project-chat-inspection";
import {
  actionType,
  formatDateTime,
  formatTimestamp,
} from "~/modules/projects/lib/project-detail-format";
import { getProjectProductLabel } from "~/modules/projects/lib/project-product";
import {
  getEditorProjectDetailsQuery,
  getProjectChatThreadsQuery,
  getProjectDetailQuery,
  getProjectOverviewQuery,
  getProjectSnapshotPlaybackQuery,
  getProjectVersionActionsQuery,
} from "~/modules/projects/queries";
import type {
  EditorProjectDetails,
  ProjectOverview,
  ProjectChatThreadSnapshot,
  ProjectSnapshot,
  ProjectSnapshotPlayback,
  ProjectSummary,
  ProjectVersionActions,
} from "~/modules/projects/schemas";

const longToShortReturnStatusSchema = z.enum([
  "QUEUED",
  "PROCESSING",
  "COMPLETE",
  "FAILED",
  "CANCELED",
]);
const PROJECT_PLAYBACK_QUERY_STALE_TIME_MS = 5 * 60 * 1000;
const PROJECT_PLAYBACK_QUERY_GC_TIME_MS = 30 * 60 * 1000;
const PROJECT_PLAYBACK_PREFETCH_DISTANCE = 2;
const PROJECT_PLAYBACK_BACKGROUND_PREFETCH_DELAY_MS = 750;

export const Route = createFileRoute("/_dash/projects/$projectId")({
  component: RouteComponent,
  validateSearch: z.object({
    ownerUserId: z.string().optional(),
    ownerOrgId: z.string().optional(),
    from: z.literal("long-to-short").optional(),
    updatedAfterMs: z.coerce.number().optional(),
    updatedBeforeMs: z.coerce.number().optional(),
    userId: z.string().optional(),
    operationId: z.string().optional(),
    workflowId: z.string().optional(),
    projectGroupId: z.string().optional(),
    status: longToShortReturnStatusSchema.optional(),
    expandedResultId: z.string().optional(),
    excludeInternalUsers: optionalTrueSearchParamSchema,
  }),
});

function RouteComponent() {
  return (
    <PermissionGuard permission="projects:view">
      <ProjectDetailPage />
    </PermissionGuard>
  );
}

async function prefetchProjectSnapshotPlayback(
  queryClient: QueryClient,
  project: ProjectSummary,
  snapshot: ProjectSnapshot,
  options: { preloadProto?: boolean } = {}
) {
  if (!project.createdBy) {
    return;
  }

  try {
    const data = await queryClient.fetchQuery(
      getProjectSnapshotPlaybackQuery.queryOptions(
        {
          pathParams: {
            projectId: project.id,
            snapshotId: snapshot.id,
          },
          params: {
            syncToken: snapshot.syncToken,
            ownerUserId: project.createdBy,
          },
        },
        {
          staleTime: PROJECT_PLAYBACK_QUERY_STALE_TIME_MS,
          gcTime: PROJECT_PLAYBACK_QUERY_GC_TIME_MS,
        }
      )
    );
    if (options.preloadProto ?? true) {
      await preloadProjectPlaybackProto(data.playback);
    }
  } catch (error) {
    void error;
  }
}

function ProjectDetailPage() {
  const queryClient = useQueryClient();
  const { projectId } = Route.useParams();
  const search = Route.useSearch();
  const [selectedSnapshotId, setSelectedSnapshotId] = useState<string | null>(null);
  const previewRef = useRef<JerboaProjectRendererPreviewHandle>(null);
  const isLongToShortReturn = search.from === "long-to-short";
  const useEditorDetails =
    isLongToShortReturn || Boolean(search.ownerUserId) || Boolean(search.ownerOrgId);
  const hasEditorDetailsOwnerContext = Boolean(search.ownerUserId);
  const backSearch = {
    updatedAfterMs: search.updatedAfterMs,
    updatedBeforeMs: search.updatedBeforeMs,
    userId: search.userId,
    operationId: search.operationId,
    workflowId: search.workflowId,
    projectGroupId: search.projectGroupId,
    status: search.status,
    expandedResultId: search.expandedResultId,
    excludeInternalUsers: search.excludeInternalUsers,
  };

  const {
    data,
    isLoading,
    error: projectError,
  } = getProjectDetailQuery.useQuery(
    {
      pathParams: { projectId },
    },
    {
      enabled: !useEditorDetails,
      retry: false,
    }
  );
  const {
    data: editorProject,
    isLoading: isEditorProjectLoading,
    isFetching: isEditorProjectFetching,
    error: editorProjectError,
  } = getEditorProjectDetailsQuery.useQuery(
    {
      pathParams: { projectId },
      params: {
        ownerUserId: search.ownerUserId ?? "",
        ownerOrgId: search.ownerOrgId,
      },
    },
    {
      enabled: useEditorDetails && hasEditorDetailsOwnerContext,
    }
  );
  const {
    data: overviewData,
    isLoading: isOverviewLoading,
    error: overviewError,
  } = getProjectOverviewQuery.useQuery(
    {
      pathParams: { projectId },
    },
    {
      enabled: !useEditorDetails,
      retry: false,
    }
  );
  const {
    data: chatData,
    isLoading: isChatLoading,
    isFetching: isChatFetching,
    error: chatError,
  } = getProjectChatThreadsQuery.useQuery({
    pathParams: { projectId },
    params: { limit: 25, messageLimit: 100 },
  });
  const chatThreads = useMemo(() => chatData?.threads ?? [], [chatData?.threads]);
  const chatLinkedSyncTokens = useMemo(
    () => collectCommittedSyncTokens(chatThreads),
    [chatThreads]
  );

  const project = data?.project ?? null;
  const defaultSnapshotId = useMemo(() => {
    if (!project) {
      return null;
    }

    const currentSnapshot = project.snapshots.find(
      (snapshot) => snapshot.id === project.currentSnapshotId
    );
    if (currentSnapshot) {
      return currentSnapshot.id;
    }

    return (
      project.snapshots.reduce<ProjectSnapshot | null>(
        (latest, snapshot) =>
          !latest || snapshot.clientCreatedAtTimestamp > latest.clientCreatedAtTimestamp
            ? snapshot
            : latest,
        null
      )?.id ?? null
    );
  }, [project]);

  useEffect(() => {
    setSelectedSnapshotId(defaultSnapshotId);
  }, [defaultSnapshotId, projectId]);

  const selectedSnapshot = useMemo(
    () => project?.snapshots.find((snapshot) => snapshot.id === selectedSnapshotId) ?? null,
    [project?.snapshots, selectedSnapshotId]
  );

  const {
    data: versionActions,
    isLoading: isVersionActionsLoading,
    error: versionActionsError,
  } = getProjectVersionActionsQuery.useQuery(
    selectedSnapshot && project?.createdBy
      ? {
          pathParams: { projectId },
          params: {
            syncToken: selectedSnapshot.syncToken,
            ownerUserId: project.createdBy,
          },
        }
      : skipToken,
    { retry: false }
  );
  const chatFallbackActions = useMemo(
    () =>
      selectedSnapshot
        ? findProjectChatActionsForSyncToken(chatThreads, selectedSnapshot.syncToken)
        : [],
    [chatThreads, selectedSnapshot]
  );

  const {
    data: playbackData,
    isLoading: isPlaybackLoading,
    isFetching: isPlaybackFetching,
    error: playbackError,
  } = getProjectSnapshotPlaybackQuery.useQuery(
    !useEditorDetails && project?.hasDataModel && selectedSnapshot && project.createdBy
      ? {
          pathParams: {
            projectId,
            snapshotId: selectedSnapshot.id,
          },
          params: {
            syncToken: selectedSnapshot.syncToken,
            ownerUserId: project.createdBy,
          },
        }
      : skipToken,
    {
      staleTime: PROJECT_PLAYBACK_QUERY_STALE_TIME_MS,
      gcTime: PROJECT_PLAYBACK_QUERY_GC_TIME_MS,
    }
  );

  const playback = playbackData?.playback ?? null;
  const [previewPlayback, setPreviewPlayback] = useState<ProjectSnapshotPlayback | null>(null);
  const editorPlayback = useMemo(
    () => (editorProject ? editorProjectDetailsToPlayback(editorProject) : null),
    [editorProject]
  );
  const productLabel = getProjectProductLabel(project?.createdByProductId);

  useEffect(() => {
    if (useEditorDetails || !project?.hasDataModel || !selectedSnapshotId) {
      return;
    }

    const selectedIndex = project.snapshots.findIndex((snapshot) => {
      return snapshot.id === selectedSnapshotId;
    });
    if (selectedIndex < 0) {
      return;
    }

    const prioritySnapshotIds = new Set<string>();
    for (let distance = 1; distance <= PROJECT_PLAYBACK_PREFETCH_DISTANCE; distance += 1) {
      const nearbySnapshots = [
        project.snapshots[selectedIndex - distance],
        project.snapshots[selectedIndex + distance],
      ];

      for (const snapshot of nearbySnapshots) {
        if (!snapshot) {
          continue;
        }
        prioritySnapshotIds.add(snapshot.id);

        void prefetchProjectSnapshotPlayback(queryClient, project, snapshot);
      }
    }

    let canceled = false;
    const timeout = window.setTimeout(() => {
      void (async () => {
        for (const snapshot of project.snapshots) {
          if (canceled) {
            return;
          }

          if (snapshot.id === selectedSnapshotId || prioritySnapshotIds.has(snapshot.id)) {
            continue;
          }

          await prefetchProjectSnapshotPlayback(queryClient, project, snapshot, {
            preloadProto: false,
          });
        }
      })();
    }, PROJECT_PLAYBACK_BACKGROUND_PREFETCH_DELAY_MS);

    return () => {
      canceled = true;
      window.clearTimeout(timeout);
    };
  }, [project, queryClient, selectedSnapshotId, useEditorDetails]);

  useEffect(() => {
    setPreviewPlayback(null);
  }, [projectId]);

  useEffect(() => {
    if (playback) {
      setPreviewPlayback(playback);
      return;
    }

    if (useEditorDetails || playbackError || !selectedSnapshotId || !project?.hasDataModel) {
      setPreviewPlayback(null);
    }
  }, [playback, playbackError, project?.hasDataModel, selectedSnapshotId, useEditorDetails]);

  const seekPreview = useCallback((timeMs: number) => {
    previewRef.current?.seek(timeMs);
  }, []);
  const subscribePreviewTick = useCallback((listener: (timeMs: number) => void) => {
    return previewRef.current?.subscribeTick(listener) ?? (() => {});
  }, []);

  if (useEditorDetails) {
    return (
      <EditorProjectDetailsView
        project={editorProject}
        playback={editorPlayback}
        isLoading={isEditorProjectLoading && hasEditorDetailsOwnerContext}
        isFetching={isEditorProjectFetching}
        error={
          hasEditorDetailsOwnerContext
            ? editorProjectError
            : new Error("Project owner user is missing from the URL")
        }
        chatThreads={chatThreads}
        isChatLoading={isChatLoading}
        isChatFetching={isChatFetching}
        chatError={chatError}
        backLink={
          isLongToShortReturn ? (
            <Link to="/ai-edits/long-to-short" search={backSearch} className="gap-1.5">
              <ChevronLeft className="size-4" aria-hidden="true" />
              Long-to-Short
            </Link>
          ) : (
            <Link to="/projects" className="gap-1.5">
              <ChevronLeft className="size-4" aria-hidden="true" />
              Projects
            </Link>
          )
        }
      />
    );
  }

  if (isLoading) {
    return <Skeleton className="h-[720px] w-full rounded-md" />;
  }

  if (!project) {
    return (
      <ProjectUnavailableChatView
        projectId={projectId}
        error={projectError}
        overview={overviewData ?? null}
        isOverviewLoading={isOverviewLoading}
        overviewError={overviewError}
        chatThreads={chatThreads}
        isChatLoading={isChatLoading}
        isChatFetching={isChatFetching}
        chatError={chatError}
      />
    );
  }

  return (
    <div className="min-w-0 max-w-full space-y-5 overflow-hidden pb-12">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects" className="gap-1.5">
            <ChevronLeft className="size-4" aria-hidden="true" />
            Projects
          </Link>
        </Button>
        <div className="min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <h1 className="truncate text-xl font-bold">{project.title || "Untitled project"}</h1>
            {project.isDeleted && (
              <Badge variant="destructive" className="shrink-0">
                Deleted
              </Badge>
            )}
          </div>
          <button
            type="button"
            onClick={() => {
              void copyToClipboard(project.id, "Project ID copied to clipboard");
            }}
            className="mt-1 block max-w-full truncate font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
            title="Copy project ID"
          >
            {project.id}
          </button>
        </div>
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1.5">
            <Smartphone className="size-3.5" aria-hidden="true" />
            Created in {productLabel}
          </Badge>
          <ProjectClientExportButton projectId={project.id} playback={playback} />
          <CloneProjectToUserButton
            projectId={project.id}
            syncToken={selectedSnapshot?.syncToken ?? project.syncToken}
          />
          <ProjectActionsMenu projectId={project.id} playback={playback} />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <MetadataItem
          label="Edited"
          value={formatTimestamp(project.editedAtTimestamp)}
          icon={<Clock />}
        />
        <MetadataItem label="User" value={project.createdBy ?? "Unknown"} icon={<User />} />
        <MetadataItem label="Sync token" value={project.syncToken ?? "Missing"} icon={<Layers />} />
      </div>

      <div className="grid min-w-0 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <div className="min-w-0 space-y-4">
          <JerboaProjectRendererPreview
            ref={previewRef}
            playback={previewPlayback}
            isLoading={(!previewPlayback && isPlaybackLoading) || isPlaybackFetching}
            playbackError={playbackError}
          />

          {previewPlayback && (
            <>
              <ProjectPlaybackTimeline
                playback={previewPlayback}
                subscribeTick={subscribePreviewTick}
                onSeek={seekPreview}
              />
              <ProjectAssetStore playback={previewPlayback} />
            </>
          )}

          <ProjectOverviewSection
            overview={overviewData ?? null}
            isLoading={isOverviewLoading}
            error={overviewError}
          />
        </div>

        <SnapshotTimeline
          snapshots={project.snapshots}
          snapshotCount={project.snapshotCount}
          selectedSnapshotId={selectedSnapshotId}
          onSelectSnapshot={setSelectedSnapshotId}
          linkedSyncTokens={chatLinkedSyncTokens}
          versionActions={versionActions ?? null}
          isVersionActionsLoading={isVersionActionsLoading}
          versionActionsError={versionActionsError}
          chatFallbackActions={chatFallbackActions}
        />
      </div>

      <ProjectChatThreadsPanel
        projectId={project.id}
        threads={chatThreads}
        isLoading={isChatLoading}
        isFetching={isChatFetching}
        error={chatError}
        snapshots={project.snapshots}
        onSelectSnapshot={setSelectedSnapshotId}
      />
    </div>
  );
}

function ProjectUnavailableChatView({
  projectId,
  error,
  overview,
  isOverviewLoading,
  overviewError,
  chatThreads,
  isChatLoading,
  isChatFetching,
  chatError,
}: {
  projectId: string;
  error: Error | null;
  overview: ProjectOverview | null;
  isOverviewLoading: boolean;
  overviewError: Error | null;
  chatThreads: ProjectChatThreadSnapshot[];
  isChatLoading: boolean;
  isChatFetching: boolean;
  chatError: Error | null;
}) {
  return (
    <div className="min-w-0 max-w-full space-y-5 overflow-hidden pb-12">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          <Link to="/projects" className="gap-1.5">
            <ChevronLeft className="size-4" aria-hidden="true" />
            Projects
          </Link>
        </Button>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold">Project chat</h1>
          <button
            type="button"
            onClick={() => {
              void copyToClipboard(projectId, "Project ID copied to clipboard");
            }}
            className="mt-1 block max-w-full truncate font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
            title="Copy project ID"
          >
            {projectId}
          </button>
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="border-amber-300 bg-amber-100 text-amber-950">
              Project metadata unavailable
            </Badge>
            <span>Chat evidence is loaded independently.</span>
          </div>
          <div className="mt-1 break-words font-mono text-xs text-amber-900">{error.message}</div>
        </div>
      )}

      <ProjectOverviewSection
        overview={overview}
        isLoading={isOverviewLoading}
        error={overviewError}
      />

      <ProjectChatThreadsPanel
        projectId={projectId}
        threads={chatThreads}
        isLoading={isChatLoading}
        isFetching={isChatFetching}
        error={chatError}
        emptyStateDescription="No chat threads have touched this project ID yet."
      />
    </div>
  );
}

function EditorProjectDetailsView({
  project,
  playback,
  isLoading,
  isFetching,
  error,
  chatThreads,
  isChatLoading,
  isChatFetching,
  chatError,
  backLink,
}: {
  project?: EditorProjectDetails | null;
  playback: ProjectSnapshotPlayback | null;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  chatThreads: ProjectChatThreadSnapshot[];
  isChatLoading: boolean;
  isChatFetching: boolean;
  chatError: Error | null;
  backLink: React.ReactNode;
}) {
  if (isLoading) {
    return <Skeleton className="h-[720px] w-full rounded-md" />;
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-sm text-destructive">
        {error.message}
      </div>
    );
  }

  if (!project) {
    return <div className="text-sm text-muted-foreground">Project not found</div>;
  }

  const productLabel = getProjectProductLabel(project.creationMetadata?.productType ?? null);

  return (
    <div className="min-w-0 max-w-full space-y-5 overflow-hidden pb-12">
      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <Button variant="outline" size="sm" asChild>
          {backLink}
        </Button>
        <div className="min-w-0">
          <h1 className="truncate text-xl font-bold">{project.title || "Untitled project"}</h1>
          <button
            type="button"
            onClick={() => {
              void copyToClipboard(project.id, "Project ID copied to clipboard");
            }}
            className="mt-1 block max-w-full truncate font-mono text-xs text-muted-foreground transition-colors hover:text-foreground"
            title="Copy project ID"
          >
            {project.id}
          </button>
        </div>
        <div className="ml-auto flex shrink-0 flex-wrap items-center gap-2">
          <Badge variant="outline" className="gap-1.5">
            <Smartphone className="size-3.5" aria-hidden="true" />
            Created in {productLabel}
          </Badge>
          <ProjectClientExportButton projectId={project.id} playback={playback} />
          <ProjectActionsMenu projectId={project.id} playback={playback} />
        </div>
      </div>

      <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-muted-foreground">
        <MetadataItem label="Sync token" value={project.syncToken} icon={<Layers />} />
        {project.projectGroupId && (
          <MetadataItem label="Project group" value={project.projectGroupId} icon={<Layers />} />
        )}
        {project.mirageStudioProjectId && (
          <MetadataItem
            label="Studio project"
            value={project.mirageStudioProjectId}
            icon={<Layers />}
          />
        )}
      </div>

      <div className="min-w-0">
        <JerboaProjectRendererPreview
          playback={playback}
          isLoading={isLoading || isFetching}
          playbackError={error}
        />
      </div>

      <ProjectChatThreadsPanel
        projectId={project.id}
        threads={chatThreads}
        isLoading={isChatLoading}
        isFetching={isChatFetching}
        error={chatError}
      />
    </div>
  );
}

function MetadataItem({
  label,
  value,
  icon,
}: {
  label: string;
  value: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex min-w-0 items-center gap-1.5">
      <span className="shrink-0 [&_svg]:size-3.5">{icon}</span>
      <span className="shrink-0">{label}</span>
      <span className="min-w-0 truncate font-medium text-foreground">{value}</span>
    </div>
  );
}

function SnapshotTimeline({
  snapshots,
  snapshotCount,
  selectedSnapshotId,
  onSelectSnapshot,
  linkedSyncTokens,
  versionActions,
  isVersionActionsLoading,
  versionActionsError,
  chatFallbackActions,
}: {
  snapshots: ProjectSnapshot[];
  snapshotCount: number;
  selectedSnapshotId: string | null;
  onSelectSnapshot: (snapshotId: string) => void;
  linkedSyncTokens: Set<string>;
  versionActions: ProjectVersionActions | null;
  isVersionActionsLoading: boolean;
  versionActionsError: Error | null;
  chatFallbackActions: unknown[];
}) {
  const orderedSnapshots = [...snapshots].sort(
    (left, right) => right.clientCreatedAtTimestamp - left.clientCreatedAtTimestamp
  );
  const selectedSnapshot =
    orderedSnapshots.find((snapshot) => snapshot.id === selectedSnapshotId) ??
    orderedSnapshots[0] ??
    null;
  const selectedSnapshotIndex = selectedSnapshot
    ? orderedSnapshots.findIndex((snapshot) => snapshot.id === selectedSnapshot.id)
    : -1;

  return (
    <div className="min-w-0 rounded-md border bg-card p-3 xl:sticky xl:top-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold">Snapshot timeline</h2>
        <Badge variant="secondary">{snapshotCount} snapshots</Badge>
      </div>
      {selectedSnapshot ? (
        <>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex w-full min-w-0 items-center gap-2 rounded-md border bg-background p-3 text-left transition-colors hover:border-primary/60"
                aria-label="Select project snapshot"
              >
                <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                  {orderedSnapshots.length - selectedSnapshotIndex}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    {selectedSnapshot.isCurrent && <Badge variant="secondary">Current</Badge>}
                    {linkedSyncTokens.has(selectedSnapshot.syncToken) && (
                      <Badge variant="outline">Chat change</Badge>
                    )}
                  </span>
                  <span className="mt-1 block truncate font-mono text-xs text-muted-foreground">
                    {selectedSnapshot.syncToken}
                  </span>
                  <span className="mt-0.5 block text-xs">
                    {formatTimestamp(selectedSnapshot.clientCreatedAtTimestamp)}
                  </span>
                </span>
                <ChevronDown className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="max-h-[min(70vh,560px)] w-[320px]">
              {orderedSnapshots.map((snapshot, index) => {
                const selected = selectedSnapshotId === snapshot.id;
                const linkedToChat = linkedSyncTokens.has(snapshot.syncToken);
                return (
                  <DropdownMenuItem
                    key={snapshot.id}
                    onSelect={() => onSelectSnapshot(snapshot.id)}
                    className="items-start p-2 data-[selected=true]:bg-accent"
                    data-selected={selected}
                  >
                    <span className="flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold">
                      {orderedSnapshots.length - index}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-2">
                        {snapshot.isCurrent && <Badge variant="secondary">Current</Badge>}
                        {linkedToChat && <Badge variant="outline">Chat change</Badge>}
                      </span>
                      <span className="mt-1 block truncate font-mono text-xs text-muted-foreground">
                        {snapshot.syncToken}
                      </span>
                      <span className="mt-0.5 block text-xs">
                        {formatTimestamp(snapshot.clientCreatedAtTimestamp)}
                      </span>
                    </span>
                    {selected && (
                      <CheckCircle2
                        className="mt-1 size-4 shrink-0 text-primary"
                        aria-hidden="true"
                      />
                    )}
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>

          <SnapshotVersionActions
            versionActions={versionActions}
            isLoading={isVersionActionsLoading}
            error={versionActionsError}
            chatFallbackActions={chatFallbackActions}
          />
        </>
      ) : (
        <div className="text-sm text-muted-foreground">No snapshots found</div>
      )}
    </div>
  );
}

function SnapshotVersionActions({
  versionActions,
  isLoading,
  error,
  chatFallbackActions,
}: {
  versionActions: ProjectVersionActions | null;
  isLoading: boolean;
  error: Error | null;
  chatFallbackActions: unknown[];
}) {
  if (isLoading) {
    return <Skeleton className="mt-3 h-24 w-full rounded-md" />;
  }

  const recordedActions = versionActions?.actions ?? [];
  const canUseChatFallback =
    !versionActions ||
    versionActions.syncType === "actions" ||
    versionActions.syncType === "unknown";
  let actions = recordedActions;
  if (actions.length === 0 && canUseChatFallback) {
    actions = chatFallbackActions;
  }

  let syncType = versionActions?.syncType;
  if ((syncType === "unknown" || !syncType) && actions.length > 0) {
    syncType = "actions";
  }

  if (syncType === "actions") {
    return (
      <div className="mt-3 min-w-0 rounded-md border bg-muted/20 p-3">
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-xs font-semibold">Changes in this version</h3>
          <Badge variant="secondary">
            {actions.length > 0
              ? `${actions.length} action${actions.length === 1 ? "" : "s"}`
              : "Payload unavailable"}
          </Badge>
        </div>

        {actions.length > 0 ? (
          <div className="mt-2 max-h-[420px] space-y-2 overflow-y-auto pr-1">
            {actions.map((action, index) => (
              <details key={index} className="rounded-md border bg-background p-2">
                <summary className="cursor-pointer break-all font-mono text-xs font-medium">
                  {index + 1}. {actionType(action)}
                </summary>
                <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-words text-xs">
                  {JSON.stringify(action, null, 2)}
                </pre>
              </details>
            ))}
          </div>
        ) : (
          <p className="mt-2 text-xs text-muted-foreground">
            Individual actions were not recorded for this historical sync.
          </p>
        )}

        <VersionCommitMetadata versionActions={versionActions} />
      </div>
    );
  }

  if (syncType === "fullProject") {
    return (
      <div className="mt-3 rounded-md border bg-muted/20 p-3">
        <Badge variant="outline">Full project sync</Badge>
        <p className="mt-2 text-xs text-muted-foreground">
          The complete project was uploaded for this version, so there are no individual project
          actions to show.
        </p>
        <VersionCommitMetadata versionActions={versionActions} />
      </div>
    );
  }

  if (syncType === "assetData") {
    return (
      <div className="mt-3 rounded-md border bg-muted/20 p-3">
        <Badge variant="outline">Asset data sync</Badge>
        <p className="mt-2 text-xs text-muted-foreground">
          This version updated asset resolution data rather than applying project actions.
        </p>
        <VersionCommitMetadata versionActions={versionActions} />
      </div>
    );
  }

  if (syncType === "undo" || syncType === "redo") {
    return (
      <div className="mt-3 rounded-md border bg-muted/20 p-3">
        <Badge variant="outline">{syncType === "undo" ? "Undo" : "Redo"} sync</Badge>
        <p className="mt-2 text-xs text-muted-foreground">
          This version {syncType === "undo" ? "undid" : "reapplied"} a previously accepted mutation.
        </p>
        <VersionCommitMetadata versionActions={versionActions} />
      </div>
    );
  }

  return (
    <div className="mt-3 rounded-md border border-dashed p-3 text-xs text-muted-foreground">
      {error ? `Version changes unavailable: ${error.message}` : "Version changes unavailable"}
    </div>
  );
}

function VersionCommitMetadata({
  versionActions,
}: {
  versionActions: ProjectVersionActions | null;
}) {
  if (!versionActions) {
    return null;
  }

  return (
    <div className="mt-3 space-y-1.5 text-xs text-muted-foreground">
      {versionActions.acceptedAt && <div>{formatDateTime(versionActions.acceptedAt)}</div>}
      <CopyableId value={versionActions.serverCommitId} label="Server commit ID" prefix="Commit" />
      <CopyableId
        value={versionActions.clientMutationId}
        label="Client mutation ID"
        prefix="Mutation"
      />
      <CopyableId
        value={versionActions.sourceOperationId}
        label="Source operation ID"
        prefix="Source op"
      />
      <CopyableId
        value={versionActions.referencedClientMutationId}
        label="Referenced client mutation ID"
        prefix="References"
      />
      <CopyableId
        value={versionActions.restoredSyncToken}
        label="Restored sync token"
        prefix="Restored"
      />
    </div>
  );
}

function ProjectActionsMenu({
  projectId,
  playback,
}: {
  projectId: string;
  playback: ProjectSnapshotPlayback | null;
}) {
  const snapshotId = playback?.snapshot.id ?? "current";
  const canDownload = Boolean(playback?.projectDataUrl);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="size-8">
          <MoreHorizontal className="size-4" aria-hidden="true" />
          <span className="sr-only">Project actions</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={!canDownload}
          onSelect={(event) => {
            event.preventDefault();
            if (playback?.projectDataUrl) {
              void downloadUrl(
                playback.projectDataUrl,
                `${safeFilenamePart(projectId)}-${safeFilenamePart(snapshotId)}.pb`
              );
            }
          }}
        >
          <Download className="size-4" aria-hidden="true" />
          Download proto
        </DropdownMenuItem>
        <DropdownMenuItem
          disabled={!playback}
          onSelect={(event) => {
            event.preventDefault();
            if (playback) {
              downloadJson(
                playback.projectJson,
                `${safeFilenamePart(projectId)}-${safeFilenamePart(snapshotId)}.json`
              );
            }
          }}
        >
          <FileJson className="size-4" aria-hidden="true" />
          Download JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

async function downloadUrl(url: string, filename: string) {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }

    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    triggerDownload(blobUrl, filename);
    URL.revokeObjectURL(blobUrl);
  } catch {
    triggerDownload(url, filename, true);
  }
}

function triggerDownload(url: string, filename: string, openInNewTab = false) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  if (openInNewTab) {
    anchor.target = "_blank";
    anchor.rel = "noreferrer";
  }
  anchor.click();
}

function downloadJson(data: unknown, filename: string) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  triggerDownload(url, filename);
  URL.revokeObjectURL(url);
}

function safeFilenamePart(value: string) {
  return value.replace(/[^a-zA-Z0-9._-]+/g, "-");
}
